import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserCheckIcon, FlaskConicalIcon, ShieldCheckIcon, ActivityIcon, HeartPulseIcon, ArrowRightIcon, StethoscopeIcon, CalendarCheckIcon, ClipboardListIcon, RefreshCcwIcon } from 'lucide-react';
import { FAQAccordion } from '../components/FAQAccordion';
export function HomePage() {
  const scrollToConditions = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const element = document.getElementById('conditions');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const fadeUpItem = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };
  const faqItems = [{
    question: 'Is TRT safe?',
    answer: 'When prescribed appropriately and monitored by a licensed medical professional, Testosterone Replacement Therapy is generally considered safe. Our protocols are strictly evidence-based, and we require regular lab work to monitor your health markers and adjust dosages as needed to minimize risks.'
  }, {
    question: 'How do telehealth visits work?',
    answer: 'Telehealth visits are conducted via a secure, HIPAA-compliant video platform. You will meet face-to-face with your provider from the comfort of your home to discuss your symptoms, review lab results, and develop or adjust your treatment plan.'
  }, {
    question: 'What lab work is required?',
    answer: 'We require comprehensive initial blood panels to accurately diagnose low testosterone and assess your overall health. Once on treatment, follow-up labs are required periodically (typically every 3-6 months) to monitor testosterone levels, hematocrit, PSA, and other critical markers.'
  }, {
    question: 'Who is eligible for TRT?',
    answer: 'Eligibility is determined by a combination of clinical symptoms of low testosterone and corresponding laboratory results showing levels below the optimal range. Our providers conduct a thorough medical history and evaluation to ensure TRT is medically appropriate for you.'
  }, {
    question: 'How quickly will I see results?',
    answer: 'Response times vary by individual. Some patients report improvements in energy and mood within 3-4 weeks, while physical changes like muscle mass and fat distribution may take 3-6 months to become noticeable. TRT is a long-term therapy, not a quick fix.'
  }, {
    question: 'What are the potential side effects?',
    answer: 'Potential side effects can include acne, fluid retention, increased red blood cell count, and changes in cholesterol levels. This is why regular lab monitoring and provider follow-ups are mandatory components of our care program.'
  }];
  return <div className="w-full" data-id="element-266">
      {/* Hero Section */}
      <section className="relative bg-white pt-20 pb-24 lg:pt-32 lg:pb-32 overflow-hidden border-b border-bordercolor" data-id="element-267">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--color-primary-light),_transparent_40%)] opacity-50 pointer-events-none" data-id="element-268" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-269">
          <div className="max-w-3xl" data-id="element-270">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-4xl md:text-5xl lg:text-6xl font-bold text-brandtext leading-tight tracking-tight mb-6" data-id="element-271">
              Telehealth Testosterone Care from Licensed Providers in{' '}
              <span className="text-primary" data-id="element-272">[STATE]</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-lg md:text-xl text-brandtext-muted mb-10 leading-relaxed max-w-2xl" data-id="element-273">
              Safe, evidence-based TRT delivered through secure telehealth. We
              combine clinical rigor with modern convenience to help you restore
              optimal hormone levels.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="flex flex-col sm:flex-row items-start sm:items-center gap-5" data-id="element-274">
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-3.5 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors shadow-sm w-full sm:w-auto" data-id="element-275">
                Schedule your tele-health appointment
              </Link>
              <a href="#conditions" onClick={scrollToConditions} className="inline-flex items-center text-primary font-medium hover:text-primary-dark transition-colors group" data-id="element-276">
                Is TRT right for me?
                <ArrowRightIcon className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-277" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Clinical Credibility Strip */}
      <section className="bg-surface py-12 border-b border-bordercolor" data-id="element-278">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-279">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-280">
            <motion.div variants={fadeUpItem} className="flex items-start gap-4 p-4 rounded-lg border border-bordercolor bg-brandbg shadow-subtle" data-id="element-281">
              <div className="p-2.5 bg-primary-light rounded-md text-primary flex-shrink-0" data-id="element-282">
                <UserCheckIcon className="w-6 h-6" data-id="element-283" />
              </div>
              <div data-id="element-284">
                <h3 className="font-semibold text-brandtext mb-1" data-id="element-285">
                  Board-certified providers
                </h3>
                <p className="text-sm text-brandtext-muted" data-id="element-286">
                  Care directed by licensed medical professionals.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUpItem} className="flex items-start gap-4 p-4 rounded-lg border border-bordercolor bg-brandbg shadow-subtle" data-id="element-287">
              <div className="p-2.5 bg-primary-light rounded-md text-primary flex-shrink-0" data-id="element-288">
                <FlaskConicalIcon className="w-6 h-6" data-id="element-289" />
              </div>
              <div data-id="element-290">
                <h3 className="font-semibold text-brandtext mb-1" data-id="element-291">
                  Evidence-based protocols
                </h3>
                <p className="text-sm text-brandtext-muted" data-id="element-292">
                  Treatments guided by current clinical standards.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUpItem} className="flex items-start gap-4 p-4 rounded-lg border border-bordercolor bg-brandbg shadow-subtle" data-id="element-293">
              <div className="p-2.5 bg-primary-light rounded-md text-primary flex-shrink-0" data-id="element-294">
                <ShieldCheckIcon className="w-6 h-6" data-id="element-295" />
              </div>
              <div data-id="element-296">
                <h3 className="font-semibold text-brandtext mb-1" data-id="element-297">
                  Secure telehealth visits
                </h3>
                <p className="text-sm text-brandtext-muted" data-id="element-298">
                  100% HIPAA-compliant private consultations.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Conditions We Help With */}
      <section id="conditions" className="py-20 bg-brandbg" data-id="element-299">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-300">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-301">
            <h2 className="text-3xl font-bold text-brandtext mb-4" data-id="element-302">
              Conditions We Help With
            </h2>
            <p className="text-brandtext-muted text-lg" data-id="element-303">
              Low testosterone can present through a variety of physical and
              cognitive symptoms. We evaluate your complete clinical picture.
            </p>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: '-100px'
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-304">
            {[{
            title: 'Low energy and fatigue',
            icon: <ActivityIcon className="w-6 h-6" data-id="element-305" />,
            desc: "Persistent exhaustion that doesn't improve with adequate sleep, often described as a 'mid-afternoon crash' or general lethargy."
          }, {
            title: 'Reduced strength',
            icon: <HeartPulseIcon className="w-6 h-6" data-id="element-306" />,
            desc: 'Noticeable decline in physical performance, loss of muscle mass, and increased difficulty recovering from exercise.'
          }, {
            title: 'Low mood & motivation',
            icon: <StethoscopeIcon className="w-6 h-6" data-id="element-307" />,
            desc: 'Cognitive symptoms including brain fog, decreased drive, irritability, and a general loss of enthusiasm for daily activities.'
          }, {
            title: 'Body composition',
            icon: <UserCheckIcon className="w-6 h-6" data-id="element-308" />,
            desc: 'Unexplained weight gain, particularly increased visceral fat around the midsection, despite maintaining diet and exercise habits.'
          }].map((item, i) => <motion.div key={i} variants={fadeUpItem} className="bg-surface border border-bordercolor p-6 rounded-xl shadow-subtle hover:shadow-card transition-shadow" data-id="element-309">
                <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center text-primary mb-5" data-id="element-310">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-brandtext mb-3" data-id="element-311">
                  {item.title}
                </h3>
                <p className="text-brandtext-muted text-sm leading-relaxed" data-id="element-312">
                  {item.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* How Telehealth TRT Works */}
      <section className="py-20 bg-white border-y border-bordercolor" data-id="element-313">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-314">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-315">
            <h2 className="text-3xl font-bold text-brandtext mb-4" data-id="element-316">
              How Telehealth TRT Works
            </h2>
            <p className="text-brandtext-muted text-lg" data-id="element-317">
              A structured, medically supervised process designed for safety and
              efficacy.
            </p>
          </div>

          <div className="relative" data-id="element-318">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-8 left-0 w-full h-0.5 bg-bordercolor -z-10" data-id="element-319"></div>

            <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{
            once: true,
            margin: '-100px'
          }} className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-6" data-id="element-320">
              {[{
              step: '01',
              title: 'Initial Evaluation',
              icon: <CalendarCheckIcon className="w-5 h-5" data-id="element-321" />,
              desc: 'Schedule a secure video consultation to discuss your symptoms and medical history.'
            }, {
              step: '02',
              title: 'Lab Work & Review',
              icon: <FlaskConicalIcon className="w-5 h-5" data-id="element-322" />,
              desc: 'Complete comprehensive blood panels at a local lab facility for provider review.'
            }, {
              step: '03',
              title: 'Treatment Plan',
              icon: <ClipboardListIcon className="w-5 h-5" data-id="element-323" />,
              desc: 'If eligible, receive a personalized, guideline-based treatment protocol.'
            }, {
              step: '04',
              title: 'Ongoing Follow-up',
              icon: <RefreshCcwIcon className="w-5 h-5" data-id="element-324" />,
              desc: 'Regular lab monitoring and telehealth check-ins to ensure safety and efficacy.'
            }].map((item, i) => <motion.div key={i} variants={fadeUpItem} className="relative flex flex-col items-center text-center md:items-start md:text-left" data-id="element-325">
                  <div className="w-16 h-16 rounded-full bg-surface border-4 border-white shadow-sm flex items-center justify-center bg-primary text-white mb-6 mx-auto md:mx-0" data-id="element-326">
                    {item.icon}
                  </div>
                  <div className="text-sm font-bold text-primary mb-2 tracking-wider" data-id="element-327">
                    STEP {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-brandtext mb-3" data-id="element-328">
                    {item.title}
                  </h3>
                  <p className="text-brandtext-muted text-sm leading-relaxed" data-id="element-329">
                    {item.desc}
                  </p>
                </motion.div>)}
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-brandbg" data-id="element-330">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-331">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-332">
            <h2 className="text-3xl font-bold text-brandtext mb-4" data-id="element-333">
              Frequently Asked Questions
            </h2>
            <p className="text-brandtext-muted text-lg" data-id="element-334">
              Clear, clinical answers about our treatment protocols.
            </p>
          </div>

          <FAQAccordion items={faqItems} data-id="element-335" />

          <div className="mt-12 text-center" data-id="element-336">
            <p className="text-brandtext-muted mb-6" data-id="element-337">
              Have more specific medical questions?
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-6 py-2.5 border border-bordercolor text-sm font-medium rounded-md text-brandtext bg-surface hover:bg-gray-50 transition-colors shadow-sm" data-id="element-338">
              Contact our clinic
            </Link>
          </div>
        </div>
      </section>
    </div>;
}